import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system); employee twin of StoreCustomFieldsPage
import FormModal from '../components/FormModal';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { StaticOptionsEditor } from '../components/OptionSourceEditor';
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, Smartphone } from 'lucide-react';
import { employeeCustomFieldsAPI, type EmployeeCustomField, type EmployeeCustomFieldType } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Skeleton } from '../components/Skeleton';
import ActiveToggle from '../components/ActiveToggle';
import { ActiveStatusPill } from '../components/ActiveStatusPill';

/**
 * Master Data → Employees → Custom Fields.
 *
 * Lets a project define the extra per-employee details it wants stored — the label,
 * the input type, and for the choice types the exact values a user may pick. Those
 * definitions drive the Employee edit modal, the bulk-upload template, and — when
 * "Show on mobile home screen" is on — the value is surfaced on the employee's
 * mobile home.
 *
 * A field is marked inactive, never deleted: stored values stay addressable, so
 * marking a field active again brings its history with it.
 */

const TYPE_LABELS: Record<EmployeeCustomFieldType, string> = {
  text: 'Free text',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / No',
  dropdown: 'Dropdown (pick one)',
  multiple_choice: 'Multi-select (pick several)',
};

const TYPE_ORDER: EmployeeCustomFieldType[] = [
  'dropdown', 'multiple_choice', 'text', 'number', 'date', 'boolean',
];

/** The two types whose value must come from the field's own option list. */
const CHOICE_TYPES = new Set<EmployeeCustomFieldType>(['dropdown', 'multiple_choice']);

/**
 * Every place an active field appears and an inactive one leaves, for the question
 * asked before marking it inactive. They all read the active-only field list: the Employees edit
 * modal, the API's bulk upload template and reader, the Employees Download (and the
 * values the roster and its Inactive tab carry), and — for a field flagged for it —
 * the mobile home screen.
 */
const fieldPlaces = (field: EmployeeCustomField): string =>
  field.showOnMobileHome
    ? 'the employee edit form, the bulk upload template, the Download and the mobile home screen'
    : 'the employee edit form, the bulk upload template and the Download';

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const apiCode = (err: unknown): string | undefined =>
  (err as { response?: { data?: { code?: string } } })?.response?.data?.code;

export default function EmployeeCustomFieldsPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [fields, setFields] = useState<EmployeeCustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<EmployeeCustomField | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Set when the API reports the edit would strand stored values. Holding it in
  // state (rather than a window.confirm) keeps the explanation on screen while the
  // user decides.
  const [overwriteWarning, setOverwriteWarning] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  // Fields whose Active/Inactive change is still on its way to the server; their
  // Actions icon is disabled until it lands.
  const [togglingIds, setTogglingIds] = useState<ReadonlySet<string>>(() => new Set());

  const [formState, setFormState] = useState<{
    label: string;
    fieldType: EmployeeCustomFieldType;
    options: string[];
    isRequired: boolean;
    showOnMobileHome: boolean;
  }>({ label: '', fieldType: 'dropdown', options: [''], isRequired: false, showOnMobileHome: false });

  const load = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      // Inactive fields are listed here (greyed, can be marked active again); the
      // employee modal asks for active ones only.
      const res = await employeeCustomFieldsAPI.list(selectedProjectId, true);
      setFields(res.data?.data || []);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load custom fields.'));
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingField(null);
    setFormState({ label: '', fieldType: 'dropdown', options: [''], isRequired: false, showOnMobileHome: false });
    setFormError(null);
    setOverwriteWarning(null);
    setIsModalOpen(true);
  };

  const openEdit = (field: EmployeeCustomField) => {
    setEditingField(field);
    setFormState({
      label: field.label,
      fieldType: field.fieldType,
      // StaticOptionsEditor always wants at least one row to render.
      options: field.options && field.options.length > 0 ? field.options : [''],
      isRequired: field.isRequired,
      showOnMobileHome: field.showOnMobileHome,
    });
    setFormError(null);
    setOverwriteWarning(null);
    setIsModalOpen(true);
  };

  const submit = async (e: React.FormEvent, confirmOverwrite = false) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setFormError(null);
    try {
      const payload = {
        label: formState.label,
        fieldType: formState.fieldType,
        // Options are sent only for the choice types; the API clears them
        // otherwise so a type switch cannot leave a stale list behind.
        options: CHOICE_TYPES.has(formState.fieldType) ? formState.options : [],
        isRequired: formState.isRequired,
        showOnMobileHome: formState.showOnMobileHome,
      };
      if (editingField) {
        await employeeCustomFieldsAPI.update(selectedProjectId, editingField.id, { ...payload, confirmOverwrite });
      } else {
        await employeeCustomFieldsAPI.create(selectedProjectId, payload);
      }
      setIsModalOpen(false);
      setOverwriteWarning(null);
      await load();
    } catch (err) {
      // FIELD_HAS_VALUES is not a failure — it is the API asking whether the user
      // means to invalidate values that employees already hold.
      if (apiCode(err) === 'FIELD_HAS_VALUES') {
        setOverwriteWarning(apiMessage(err, 'This change affects stored values.'));
      } else {
        setFormError(apiMessage(err, 'Failed to save the field.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Flip one field in place, so the row keeps its position in the table. */
  const setFieldActive = (fieldId: string, active: boolean) =>
    setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, active } : f)));

  const changeActive = async (field: EmployeeCustomField, next: boolean) => {
    if (!selectedProjectId || togglingIds.has(field.id)) return;
    // Marking inactive takes the field out of every place fieldPlaces lists, so it is
    // confirmed; marking active only brings it back and needs no question.
    if (!next && !window.confirm(
      `Mark '${field.label}' inactive? It is hidden from ${fieldPlaces(field)}. Values already saved are kept, and you can switch it back on any time.`,
    )) return;

    setError(null);
    setTogglingIds((prev) => new Set(prev).add(field.id));
    setFieldActive(field.id, next);
    try {
      // Same reversible call as before — the field's active flag, never a DELETE.
      await employeeCustomFieldsAPI.setActive(selectedProjectId, field.id, next);
    } catch (err) {
      setFieldActive(field.id, field.active);
      setError(apiMessage(err, 'Failed to update the field.'));
    } finally {
      setTogglingIds((prev) => {
        const rest = new Set(prev);
        rest.delete(field.id);
        return rest;
      });
    }
  };

  const handleDrop = async (targetId: string) => {
    if (!selectedProjectId || !dragId || dragId === targetId) return;
    const ordered = fields.filter((f) => f.active);
    const from = ordered.findIndex((f) => f.id === dragId);
    const to = ordered.findIndex((f) => f.id === targetId);
    if (from < 0 || to < 0) return;

    const next = ordered.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // Re-render optimistically from the new order — without this the row snaps
    // back until the reload lands and the drag reads as having done nothing.
    // Inactive rows keep their slots; only the active ones are reordered.
    let slot = 0;
    setFields(fields.map((f) => (f.active ? next[slot++] : f)));
    setDragId(null);

    try {
      await employeeCustomFieldsAPI.reorder(selectedProjectId, next.map((f) => f.id));
    } catch (err) {
      setError(apiMessage(err, 'Failed to reorder fields.'));
      await load();
    }
  };

  const addButton = (
    <button
      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      onClick={openCreate}
    >
      Add Custom Field
    </button>
  );

  const isChoice = CHOICE_TYPES.has(formState.fieldType);

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Custom Fields</h1>
          <p className="mt-1 text-sm text-gray-500">
            Extra per-employee details for this project. They appear on the employee edit modal,
            in the bulk-upload template, and — when flagged — on the employee's mobile home screen.
          </p>
        </div>
      )}

      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      {error && <FormErrorBanner message={error} />}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="w-10 px-4 py-4" />
                <th className="whitespace-nowrap px-6 py-4">Field Name</th>
                <th className="whitespace-nowrap px-6 py-4">Type</th>
                <th className="px-6 py-4">Allowed Values</th>
                <th className="whitespace-nowrap px-6 py-4">Required</th>
                <th className="whitespace-nowrap px-6 py-4">Mobile Home</th>
                <th className="whitespace-nowrap px-6 py-4">Status</th>
                <th className="sticky right-0 z-10 whitespace-nowrap bg-gray-100 px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-4"><Skeleton className="h-4 w-4" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-28" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-56" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-20" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                  </tr>
                ))
              ) : fields.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500">
                    No custom fields yet. Add one to start capturing extra details on every employee
                    in this project — they will appear on the employee edit modal, in the bulk
                    upload template, and (when flagged) on the mobile home screen.
                  </td>
                </tr>
              ) : (
                fields.map((field) => (
                  <tr
                    key={field.id}
                    draggable={field.active}
                    onDragStart={() => setDragId(field.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(field.id)}
                    className={field.active ? 'group hover:bg-gray-50' : 'bg-gray-50 text-gray-400'}
                  >
                    <td className="px-4 py-4">
                      {field.active && <GripVertical className="h-4 w-4 cursor-grab text-gray-300" />}
                    </td>
                    <td className={`whitespace-nowrap px-6 py-4 font-medium ${field.active ? 'text-gray-900' : 'text-gray-400'}`}>
                      {/* No "Inactive" pill here: the Status column already says it. */}
                      {field.label}
                    </td>
                    <td className={`whitespace-nowrap px-6 py-4 ${field.active ? 'text-gray-600' : 'text-gray-400'}`}>
                      {TYPE_LABELS[field.fieldType] ?? field.fieldType}
                    </td>
                    <td className={`px-6 py-4 ${field.active ? 'text-gray-600' : 'text-gray-400'}`}>
                      {field.options && field.options.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {field.options.map((o) => (
                            <span key={o} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{o}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className={`whitespace-nowrap px-6 py-4 ${field.active ? 'text-gray-600' : 'text-gray-400'}`}>
                      {field.isRequired ? 'Yes' : 'No'}
                    </td>
                    <td className={`whitespace-nowrap px-6 py-4 ${field.active ? 'text-gray-600' : 'text-gray-400'}`}>
                      {field.showOnMobileHome ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          <Smartphone className="h-3 w-3" /> Shown
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <ActiveStatusPill active={field.active} />
                    </td>
                    <td className={`sticky right-0 z-10 whitespace-nowrap px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] ${field.active ? 'bg-white group-hover:bg-gray-50' : 'bg-gray-50'}`}>
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit ${field.label}`} type="button" onClick={() => openEdit(field)} />
                        {/* Inactive, not deleted — stored values stay addressable, so
                            marking a field active again brings its history with it. */}
                        <ActiveToggle
                          active={field.active}
                          onToggle={() => void changeActive(field, !field.active)}
                          label={field.label}
                          onLabel="Mark inactive"
                          offLabel="Mark active"
                          disabled={togglingIds.has(field.id)}
                        />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingField ? 'Edit Custom Field' : 'Add Custom Field'}
        size="md"
        formId="employee-custom-field-form"
        isSubmitting={isSubmitting}
        submitLabel={overwriteWarning ? 'Save anyway' : 'Save'}
      >
        <form
          id="employee-custom-field-form"
          onSubmit={(e) => submit(e, Boolean(overwriteWarning))}
          className="space-y-4"
        >
          {formError && <FormErrorBanner message={formError} />}

          {overwriteWarning && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {overwriteWarning} Saving again will keep those values in the database but they
              will no longer be valid for this field.
            </div>
          )}

          <div>
            <label htmlFor="ecf-label" className="mb-1 block text-sm font-medium text-gray-700">
              Field Name
            </label>
            <input
              id="ecf-label"
              type="text"
              value={formState.label}
              onChange={(e) => setFormState((p) => ({ ...p, label: e.target.value }))}
              placeholder="e.g. Seed Score"
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              This is the label on the employee modal and the column heading in the bulk upload sheet.
            </p>
          </div>

          <div>
            <label htmlFor="ecf-type" className="mb-1 block text-sm font-medium text-gray-700">
              Input Type
            </label>
            <select
              id="ecf-type"
              value={formState.fieldType}
              onChange={(e) => setFormState((p) => ({ ...p, fieldType: e.target.value as EmployeeCustomFieldType }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {TYPE_ORDER.map((t) => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>

          {isChoice && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Allowed Values</label>
              <p className="mb-2 text-xs text-gray-500">
                Users pick from this list — nothing else can be saved for this field, including
                through bulk upload.
              </p>
              <StaticOptionsEditor
                options={formState.options}
                onChange={(next) => setFormState((p) => ({ ...p, options: next }))}
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formState.isRequired}
              onChange={(e) => setFormState((p) => ({ ...p, isRequired: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Required — a value must be set for this field
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formState.showOnMobileHome}
              onChange={(e) => setFormState((p) => ({ ...p, showOnMobileHome: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="inline-flex items-center gap-1">
              <Smartphone className="h-4 w-4 text-gray-400" />
              Show this field's value on the employee's mobile home screen
            </span>
          </label>
        </form>
      </FormModal>
    </div>
  );
}
